import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { 
  DomainType, 
  DomainTheme,
  DomainThemeContextType, 
  DOMAIN_THEMES, 
  DOMAIN_STORAGE_KEY, 
  DEFAULT_DOMAIN 
} from '../types/domain';
import { supabaseCSSVarsTheme } from '../theme/supabaseTheme';

const DARK_MODE_STORAGE_KEY = 'decoy-dark-mode';

/**
 * DomainThemeContext
 * 
 * Provides domain-based theming for DECOY branding system
 * Features:
 * - Three domain themes: VRP, Fleet, Enterprise
 * - Persistent domain preference in localStorage
 * - Dynamic theme switching with smooth transitions
 * - Integration with existing Supabase theme system
 */

const DomainThemeContext = createContext<DomainThemeContextType | null>(null);

export interface DomainThemeProviderProps {
  children: ReactNode;
  /** Initial domain override (for testing) */
  initialDomain?: DomainType;
}

/**
 * Custom hook to use domain theme context
 */
export const useDomainTheme = (): DomainThemeContextType => {
  const context = useContext(DomainThemeContext);
  if (!context) {
    throw new Error('useDomainTheme must be used within a DomainThemeProvider');
  }
  return context;
};

/**
 * Create domain-specific MUI theme by extending Supabase theme
 */
const createDomainTheme = (domainTheme: DomainTheme, darkMode: boolean = false) => {
  const baseTheme = createTheme(supabaseCSSVarsTheme, {
    palette: {
      ...supabaseCSSVarsTheme.palette,
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: domainTheme.colors.primary,
        light: domainTheme.colors.accent,
        dark: domainTheme.colors.secondary,
      },
      secondary: {
        main: domainTheme.colors.secondary,
      },
      background: {
        default: darkMode ? '#0F172A' : (domainTheme.colors.background || '#F3F4F6'),
        paper: darkMode ? '#1F2937' : (domainTheme.colors.surface || '#FFFFFF'),
      },
      text: {
        primary: darkMode ? '#F9FAFB' : '#111827',
        secondary: darkMode ? '#D1D5DB' : '#6B7280',
      },
    },
  });

  // Add custom CSS variables for Supabase design system
  return createTheme(baseTheme, {
    cssVariables: {
      colorSchemes: {
        light: {
          palette: {
            ...baseTheme.palette,
            // Supabase design tokens - light mode
            'surface-primary': '#FFFFFF',
            'surface-secondary': '#F9FAFB',
            'surface-background': '#F3F4F6',
            'surface-hover': '#F3F4F6',
            'border-primary': '#D1D5DB',
            'border-muted': '#E5E7EB',
            'border-light': '#F3F4F6',
            'text-primary': '#111827',
            'text-secondary': '#6B7280',
            'text-muted': '#9CA3AF',
            // Domain-specific colors
            'domain-primary': domainTheme.colors.primary,
            'domain-accent': domainTheme.colors.accent,
            'domain-surface': domainTheme.colors.surface || '#FFFFFF',
          },
        },
        dark: {
          palette: {
            ...baseTheme.palette,
            // Supabase design tokens - dark mode
            'surface-primary': '#1F2937',
            'surface-secondary': '#111827',
            'surface-background': '#0F172A',
            'surface-hover': '#374151',
            'border-primary': '#374151',
            'border-muted': '#374151',
            'border-light': '#1F2937',
            'text-primary': '#F9FAFB',
            'text-secondary': '#D1D5DB',
            'text-muted': '#9CA3AF',
            // Domain-specific colors (maintain brand identity in dark mode)
            'domain-primary': domainTheme.colors.primary,
            'domain-accent': domainTheme.colors.accent,
            'domain-surface': '#1F2937',
          },
        },
      },
    },
    // Add smooth transitions for theme switching
    transitions: {
      ...baseTheme.transitions,
      create: (props, options = {}) => {
        return baseTheme.transitions.create(props, {
          duration: 200,
          easing: 'ease-in-out',
          ...options,
        });
      },
    },
  });
};

/**
 * DomainThemeProvider Component
 * 
 * Wraps application with domain-aware theming
 * Handles:
 * - Domain switching and persistence
 * - Dark mode switching and persistence
 * - Dynamic theme generation
 * - Integration with MUI ThemeProvider
 */
export const DomainThemeProvider: React.FC<DomainThemeProviderProps> = ({
  children,
  initialDomain,
}) => {
  // Load domain from localStorage or use default
  const [currentDomain, setCurrentDomain] = useState<DomainType>(() => {
    if (initialDomain) return initialDomain;
    
    try {
      const stored = localStorage.getItem(DOMAIN_STORAGE_KEY);
      if (stored && Object.keys(DOMAIN_THEMES).includes(stored)) {
        return stored as DomainType;
      }
    } catch (error) {
      console.warn('Failed to load domain preference from localStorage:', error);
    }
    return DEFAULT_DOMAIN;
  });

  // Load dark mode preference from localStorage
  const [darkMode, setDarkModeState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(DARK_MODE_STORAGE_KEY);
      return stored === 'true';
    } catch (error) {
      console.warn('Failed to load dark mode preference from localStorage:', error);
      return false;
    }
  });

  // Persist domain changes to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(DOMAIN_STORAGE_KEY, currentDomain);
      console.log(`🦆 [DomainTheme] Switched to ${currentDomain} domain`);
    } catch (error) {
      console.warn('Failed to save domain preference to localStorage:', error);
    }
  }, [currentDomain]);

  // Persist dark mode changes to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(DARK_MODE_STORAGE_KEY, darkMode.toString());
      console.log(`🌙 [DomainTheme] Dark mode ${darkMode ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.warn('Failed to save dark mode preference to localStorage:', error);
    }
  }, [darkMode]);

  // Set domain with validation
  const setDomain = (domain: DomainType) => {
    if (Object.keys(DOMAIN_THEMES).includes(domain)) {
      setCurrentDomain(domain);
    } else {
      console.warn(`Invalid domain: ${domain}. Available domains:`, Object.keys(DOMAIN_THEMES));
    }
  };

  // Dark mode controls
  const setDarkMode = (enabled: boolean) => {
    setDarkModeState(enabled);
  };

  const toggleDarkMode = () => {
    setDarkModeState(prev => !prev);
  };

  // Get theme for specific domain
  const getDomainTheme = (domain?: DomainType): DomainTheme => {
    const targetDomain = domain || currentDomain;
    return DOMAIN_THEMES[targetDomain];
  };

  // Available domains
  const availableDomains: DomainType[] = Object.keys(DOMAIN_THEMES) as DomainType[];

  // Create context value
  const contextValue: DomainThemeContextType = {
    currentDomain,
    setDomain,
    getDomainTheme,
    availableDomains,
    darkMode,
    setDarkMode,
    toggleDarkMode,
  };

  // Generate current theme with dark mode consideration
  const currentTheme = createDomainTheme(getDomainTheme(), darkMode);
  
  // Ensure CSS Variables color scheme is synchronized with dark mode state
  React.useEffect(() => {
    // Set the color scheme on the document root for CSS Variables
    const root = document.documentElement;
    root.setAttribute('data-mui-color-scheme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // Debug logging in development
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🦆 [DomainTheme] Current domain:', currentDomain);
      console.log('🎨 [DomainTheme] Theme colors:', getDomainTheme().colors);
    }
  }, [currentDomain]);

  return (
    <DomainThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={currentTheme}>
        {children}
      </ThemeProvider>
    </DomainThemeContext.Provider>
  );
};

/**
 * Hook to get current domain colors for inline styling
 */
export const useDomainColors = () => {
  const { getDomainTheme } = useDomainTheme();
  return getDomainTheme().colors;
};

/**
 * Hook to get current domain info
 */
export const useDomainInfo = () => {
  const { currentDomain, getDomainTheme } = useDomainTheme();
  const theme = getDomainTheme();
  
  return {
    domain: currentDomain,
    name: theme.name,
    description: theme.description,
    colors: theme.colors,
    logo: theme.logo,
  };
};

export default DomainThemeProvider;